import { Flex } from '@atlaskit/primitives';
import { AvatarTag } from '@atlaskit/tag';
import { DatabaseFieldType } from '@databases/types/database';
import { UserCard } from '@databases/user';
import {
  MultiValueChangeType,
  type ValueChange
} from '@databases/utils/database/changesets';
import React, { useCallback } from 'react';

import { filterOutRemovedValue } from '../popover-edit-common';

import { type ResolvedUser, type Value } from './types';

interface UserPopoverValueProps {
  user: ResolvedUser;
  resolvedUsers: ResolvedUser[];
  onChange: (value: Value[], change: ValueChange | null) => void;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  removeMentionNotification: (mentionLocalId: string, fieldId: string) => void;
  fieldId: string;
}

export const UserPopoverValue: React.FunctionComponent<UserPopoverValueProps> = ({
  user,
  resolvedUsers,
  onChange,
  addDataSectionRef,
  removeMentionNotification,
  fieldId
}) => {
  const userValueIndex = resolvedUsers.findIndex(
    (value) =>
      value &&
      'accountId' in value &&
      'accountId' in user &&
      value.accountId === user.accountId &&
      'displayName' in value &&
      'displayName' in user &&
      value.displayName === user.displayName
  );
  const remainingValues = filterOutRemovedValue({
    currentValues: resolvedUsers,
    indexToRemove: userValueIndex
  });

  const getUserDisplayName = (user: ResolvedUser): string | undefined => {
    if (user !== null && 'displayName' in user) {
      return user.displayName;
    }
    return '';
  };

  const getUserAccountId = (user: ResolvedUser): string => {
    if (user !== null && 'accountId' in user) {
      return user.accountId;
    }
    return '';
  };

  const onItemDelete = useCallback(() => {
    removeMentionNotification(user._id, fieldId);

    onChange(remainingValues, {
      type: DatabaseFieldType.USER,
      delta: {
        type: MultiValueChangeType.REMOVE,
        index: userValueIndex
      }
    });

    // focus the select input after each item removal
    addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
  }, [
    addDataSectionRef,
    onChange,
    userValueIndex,
    remainingValues,
    removeMentionNotification,
    user._id,
    fieldId
  ]);

  const displayName = getUserDisplayName(user) ?? '';
  const accountId = getUserAccountId(user);

  return (
    <Flex testId="user-popover-value" justifyContent="start" alignItems="center">
      <AvatarTag
        type="user"
        text={displayName}
        avatar={() => (
          <UserCard
            accountId={accountId}
            displayName={displayName}
            variant="avatar"
            size="xsmall"
            avatarOnly
          />
        )}
        isRemovable
        onBeforeRemoveAction={() => {
          onItemDelete();
          return true;
        }}
      />
    </Flex>
  );
};
